import React, { useEffect, useRef, useState } from 'react';
import { DURATION, SWIPE_THRESHOLD, DISMISS_TIMEOUT } from './Toast.constants';
import * as S from './Toast.styled';
import { useToastStore } from '@/store/useToastStore';

interface ToastProps {
  id?: string;
  isOpen: boolean;
  text: string;
  image?: string;
  icon?: React.ReactNode;
  action?: string;
  isLight?: boolean;
  size?: 'default' | 'compact';
  borderRadius?: number;
  duration?: number;
  onClose?: () => void;
  onAnimationComplete?: () => void;
  onActionClick?: () => void;
}

const ToastComponent: React.FC<ToastProps> = ({
  id,
  text,
  isOpen,
  image = '',
  icon,
  action = '',
  isLight = false,
  size = 'default',
  borderRadius,
  duration = DURATION,
  onClose,
  onAnimationComplete,
  onActionClick,
}) => {
  const swipeStartY = useRef<number | null>(null);
  const dismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [isVisible, setIsVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      const showTimer = setTimeout(() => {
        setIsVisible(true);
      }, 200);
      clearDismissTimer();
      startDismissTimer(duration);
      return () => clearTimeout(showTimer);
    }

    setIsVisible(false);
    clearDismissTimer();
  }, [isOpen, duration]);

  useEffect(() => {
    return () => {
      onClose?.();
      setIsVisible(false);
      clearDismissTimer();
      setShouldRender(false);
      onAnimationComplete?.();
    };
  }, []);

  useEffect(() => {
    if (!isVisible && shouldRender) {
      const unmountTimer = setTimeout(() => {
        onClose?.();
        setShouldRender(false);
        onAnimationComplete?.();
      }, 300);
      return () => clearTimeout(unmountTimer);
    }
  }, [isVisible, shouldRender, onClose, onAnimationComplete]);

  const clearDismissTimer = () => {
    if (dismissTimerRef.current) {
      clearTimeout(dismissTimerRef.current);
      dismissTimerRef.current = null;
    }
  };

  const startDismissTimer = (timeout: number) => {
    clearDismissTimer();
    dismissTimerRef.current = setTimeout(() => {
      setIsVisible(false);
    }, timeout);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    swipeStartY.current = e.touches[0].clientY;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    const endY = e.changedTouches[0].clientY;
    if (
      swipeStartY.current !== null &&
      endY - swipeStartY.current > SWIPE_THRESHOLD
    ) {
      setIsVisible(false);
      clearDismissTimer();
    }
    swipeStartY.current = null;
  };

  const handleMouseEnter = () => {
    clearDismissTimer();
  };

  const handleMouseLeave = () => {
    startDismissTimer(DISMISS_TIMEOUT);
  };

  const handleActionClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onActionClick?.();
    setIsVisible(false);
    clearDismissTimer();
  };

  if (!shouldRender) return null;

  return (
    <S.Toast
      data-testid={id ? `toast-${id}` : 'toast-component'}
      $isLight={isLight}
      $isVisible={isVisible}
      $size={size}
      $borderRadius={borderRadius}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <S.Container>
        {image && <S.Image src={image} alt="Toast" />}
        {icon && <S.Icon>{icon}</S.Icon>}
        <S.Text>{text}</S.Text>
      </S.Container>
      {action && (
        <S.Button type="button" onClick={handleActionClick}>
          {action}
        </S.Button>
      )}
    </S.Toast>
  );
};

export const Toast = () => {
  const { activeToast, isOpen, closeToast, onAnimationComplete } =
    useToastStore();

  return (
    <ToastComponent
      isOpen={isOpen}
      onClose={closeToast}
      text={activeToast?.text ?? ''}
      image={activeToast?.image}
      icon={activeToast?.icon}
      duration={activeToast?.duration}
      action={activeToast?.action}
      isLight={activeToast?.isLight}
      size={activeToast?.size}
      borderRadius={activeToast?.borderRadius}
      onAnimationComplete={onAnimationComplete}
      onActionClick={activeToast?.onActionClick}
    />
  );
};
